import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Container,
    Paper,
    Button,
    CircularProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Divider
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowLeft, Bell } from 'lucide-react';
import ParentNavbar from '../student/ParentNavbar';
import { getParentNotifications } from '../../services/parentService';

const ParentStudentNotifications: React.FC = () => {
    const { childId } = useParams<{ childId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const studentName = location.state?.studentName || 'Student';

    const [notifications, setNotifications] = useState<any[]>([]);
    const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const observer = useRef<IntersectionObserver | null>(null);

    const fetchChildNotifications = async (pageNumber: number, append = false) => {
        if (!childId) return;

        if (!append) setIsLoadingNotifications(true);
        else setIsFetchingMore(true);

        try {
            const data = await getParentNotifications(childId, pageNumber, 15);
            setNotifications(prev => append ? [...prev, ...(data.notifications || [])] : (data.notifications || []));
            setHasMore(data.hasMore);
        } catch (error) {
            console.error('Failed to load child notifications', error);
        } finally {
            setIsLoadingNotifications(false);
            setIsFetchingMore(false);
        }
    };

    useEffect(() => {
        setPage(1);
        setHasMore(true);
        fetchChildNotifications(1);
    }, [childId]);

    const lastNotificationElementRef = useCallback((node: HTMLLIElement) => {
        if (isLoadingNotifications || isFetchingMore) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => {
                    const nextPage = prevPage + 1;
                    fetchChildNotifications(nextPage, true);
                    return nextPage;
                });
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoadingNotifications, isFetchingMore, hasMore, childId]);

    return (
        <Box sx={(theme) => ({ minHeight: '100vh', backgroundColor: theme.palette.background.default })}>
            <ParentNavbar />

            <Container maxWidth="lg" sx={{ mt: 12, mb: 4 }}>
                <Button
                    startIcon={<ArrowLeft size={18} />}
                    onClick={() => navigate(`/parent/student/${childId}`, { state: { studentName } })}
                    sx={(theme) => ({
                        mb: 3,
                        textTransform: 'none',
                        color: 'text.secondary',
                        '&:hover': { backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.1 : 0.04) }
                    })}
                >
                    Back to {studentName}'s Profile
                </Button>

                <Paper sx={(theme) => ({
                    p: { xs: 2.5, sm: 4 },
                    borderRadius: 3,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 14px 40px rgba(0, 0, 0, 0.35)'
                        : '0 4px 20px rgba(0,0,0,0.05)'
                })}>
                    <Box sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3,
                        pb: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`
                    })}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #ff9f43, #feca57)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                mr: 2,
                                boxShadow: '0 4px 12px rgba(255, 159, 67, 0.35)',
                            }}
                        >
                            <Bell size={22} />
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight="700" color="text.primary">
                                Activity Notifications
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                All recent alerts and messages for {studentName}
                            </Typography>
                        </Box>
                    </Box>

                    {isLoadingNotifications ? (
                        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={28} sx={{ color: '#ff9f43' }} />
                        </Box>
                    ) : notifications.length === 0 ? (
                        <Box sx={(theme) => ({
                            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.16) : '#fff7ed',
                            borderRadius: 3,
                            p: 4,
                            textAlign: 'center',
                            border: `1px dashed ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`
                        })}>
                            <Typography variant="body1" color="text.secondary">
                                No new notifications for {studentName} yet.
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {notifications.map((note, idx) => {
                                const isLastElement = notifications.length === idx + 1;
                                return (
                                    <React.Fragment key={`${note._id}-${idx}`}>
                                        <ListItem
                                            ref={isLastElement ? lastNotificationElementRef : null}
                                            alignItems="flex-start"
                                            sx={(theme) => ({
                                                px: 2,
                                                py: 2.5,
                                                borderRadius: 2,
                                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.05) }
                                            })}
                                        >
                                            <ListItemAvatar sx={{ mt: 0 }}>
                                                <Avatar sx={(theme) => ({
                                                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.22) : '#ffeaa7',
                                                    color: theme.palette.mode === 'dark' ? theme.palette.warning.light : '#d35400',
                                                    width: 44,
                                                    height: 44
                                                })}>
                                                    <Bell size={20} />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" fontWeight={note.read ? 400 : 500} color="text.primary" sx={{ lineHeight: 1.4 }}>
                                                        {note.message}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                        {new Date(note.createdAt).toLocaleString(undefined, {
                                                            weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                                        })}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                        {idx < notifications.length - 1 && <Divider sx={(theme) => ({ mx: 2, borderColor: theme.palette.divider })} />}
                                    </React.Fragment>
                                );
                            })}

                            {isFetchingMore && (
                                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress size={24} sx={{ color: '#ff9f43' }} />
                                </Box>
                            )}

                            {!hasMore && notifications.length > 0 && (
                                <Box sx={{ py: 3, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No more notifications.
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default ParentStudentNotifications;
